import type { Agent, BlockRef, Persisted } from '@tunasong/schemas'
import { getAgentDisplayName } from '../agent-visuals.js'
import { openInlineAsk } from './inline-ask.js'
import { resolveBlockAnchorEl } from './resolve-block-anchor.js'

/**
 * "Ask <name> here…": the free-text in-context ask from the block menu, the palette and `mod+j` (spec 2026-07-23,
 * InlineAskField; Joni). One Command id, so the hotkey and the palette entry are the same thing and rebindable.
 */
export const askHereCommandId = 'agent-ask-here'

/** The shipped default; the user's binding wins wherever the hint is shown. */
export const askHereDefaultHotkey = 'mod+j'

/**
 * Which agent "Ask here" asks when several are attached: the one active in the sidebar, else the entity's
 * primary (the first attached). No picker: someone who wants another agent switches in the sidebar (Joni).
 */
export const resolveAskAgent = (
  agents: readonly Persisted<Agent>[],
  activeAgentId: string | null | undefined
): Persisted<Agent> | undefined => agents.find(agent => agent.id === activeAgentId) ?? agents[0]

/** "Ask Nils here…", or "Nils is answering…" while its turn is still in flight (a disabled state that clears itself). */
export const askHereLabel = (agent: Pick<Agent, 'name' | 'definitionId'>, answering: boolean) => {
  const name = getAgentDisplayName(agent)
  return answering ? `${name} is answering…` : `Ask ${name} here…`
}

/**
 * What an ask is about, for the docked chip (Joni): the nearest enclosing section's heading as the editor shows
 * it ("Verse 2", the ref's `summary`), else its type, else "this line". `blocks` is nearest-first (the block,
 * then its line, then its section).
 */
export const aboutLabelOf = (blocks: readonly BlockRef[]): string => {
  const section = blocks.find(block => block.type === 'section')
  return section?.summary?.trim() || section?.sectionType?.trim() || 'this line'
}

/** Open the inline ask field for `agent` at `block` (the nearest of `context`), about that block. */
export const openAskHere = (agent: Persisted<Agent>, block: BlockRef, context: readonly BlockRef[] = [block]) =>
  openInlineAsk({
    agentId: agent.id,
    agentName: getAgentDisplayName(agent),
    focus: block,
    trigger: { type: 'chat' },
    anchorEl: resolveBlockAnchorEl(block.id),
    aboutLabel: aboutLabelOf(context),
  })
